// Draft metadata and attachments are one ordered operation. A File is never
// considered persisted until the attachment API returns its durable receipt.
export const sameCityId = (left, right) => left != null && right != null && String(left) === String(right);

export function savedCityOption(id, name, cities = []) {
  if (id == null || id === '') return null;
  return cities.find(city => sameCityId(city.id, id))
    || (name ? { id, name } : null);
}

export function applicationDraftPayload(form, savedPlan = null) {
  const payload = {
    phone: form.client_phone?.trim() || '',
    last_name: form.client_last_name?.trim() || '',
    name: form.client_name?.trim() || '',
    patronymic: form.client_patronymic?.trim() || '',
    address: form.address?.trim() || '',
    object_type: form.object_type || '',
    square: form.area === '' || form.area == null ? null : Number(form.area),
    city: form.city ? Number(form.city) : null,
    custom_city_name: form.city ? '' : form.custom_city_name?.trim() || '',
    programs: (form.programs || []).map(Number),
    tz: form.tz || '',
    comment: form.comment || '',
    contact_name: form.contact_name?.trim() || '',
    contact_phone: form.contact_phone?.trim() || '',
    contact_comment: form.contact_comment || '',
    desired_time: form.desired_time || '',
    desired_date: form.desired_date || null,
    desired_dates: [...(form.desired_dates || [])],
    preferred_time_from: form.preferred_time_from || null,
    preferred_time_to: form.preferred_time_to || null,
    latitude: form.latitude ?? null,
    longitude: form.longitude ?? null,
    transfer_distance_is_manual: false,
  };
  if (form.plan_file && form.plan_file !== savedPlan) payload.plan_file = form.plan_file;
  return payload;
}

export function createApplicationDraftPersistence({ initialId = null, saveDraft, uploadFile, deleteFile, makeId }) {
  let id = initialId;
  let metadataKey = null;
  let savedPlan = null;
  let lastApplication = null;
  let tail = Promise.resolve();
  const draftUid = initialId ? null : makeId();
  const uploads = new Map();
  const removed = new Set();

  const uploadState = file => {
    if (!uploads.has(file) || uploads.get(file).cancelled) uploads.set(file, { token: makeId(), receipt: null, attempted: false, cancelled: false });
    return uploads.get(file);
  };

  return {
    get id() { return id; },
    fileReceipt(file) { const state = uploads.get(file); return state?.cancelled ? null : state?.receipt || null; },
    fileUploadId(file) { return uploadState(file).token; },
    fileWasRemoved(fileId) { return removed.has(String(fileId)); },
    planWasSaved(file) { return !!file && file === savedPlan; },
    isPersisted(form) {
      const payload = applicationDraftPayload(form, savedPlan);
      const key = JSON.stringify({ ...payload, plan_file: undefined });
      return !!id && key === metadataKey && !payload.plan_file && !form.files_preparing && !form.plan_preparing
        && (form.input_files || []).every(file => uploads.get(file)?.receipt)
        && (form.removed_file_ids || []).every(fileId => removed.has(String(fileId)))
        && [...uploads].every(([file, state]) => !state.attempted || state.cancelled
          || (form.input_files || []).includes(file)
          || (form.existing_files || []).some(item => sameCityId(item.id, state.receipt?.id))
          || removed.has(String(state.receipt?.id)));
    },
    save(form, { onMetadata, onFile, onDelete } = {}) {
      const snapshot = {
        ...form,
        programs: [...(form.programs || [])],
        desired_dates: [...(form.desired_dates || [])],
        input_files: [...(form.input_files || [])],
        existing_files: [...(form.existing_files || [])],
        removed_file_ids: [...(form.removed_file_ids || [])],
      };
      const run = async () => {
        const payload = applicationDraftPayload(snapshot, savedPlan);
        const key = JSON.stringify({ ...payload, plan_file: undefined });
        if (!id || key !== metadataKey) {
          // Keep clears (nulls and empty program lists) in JSON. Multipart plan
          // upload is a separate partial patch after this metadata is durable.
          const metadata = { ...payload };
          delete metadata.plan_file;
          const result = await saveDraft({ ...metadata, draft_id: id || undefined,
            ...(draftUid ? { client_draft_uid: draftUid } : {}) });
          if (result?.status !== 'ok' || !result.draft_id) {
            throw new Error(result?.detail || 'Не удалось сохранить черновик');
          }
          id = result.draft_id;
          metadataKey = key;
          lastApplication = result.application || lastApplication;
          onMetadata?.({ id, application: lastApplication });
        }
        if (payload.plan_file) {
          const result = await saveDraft({ draft_id: id, phone: snapshot.client_phone?.trim() || '', plan_file: payload.plan_file });
          if (result?.status !== 'ok' || !result.draft_id) {
            throw new Error(result?.detail || 'Не удалось сохранить план помещения в черновике');
          }
          savedPlan = payload.plan_file;
          lastApplication = result.application || lastApplication;
          onMetadata?.({ id, application: lastApplication });
        }
        for (const fileId of snapshot.removed_file_ids) {
          if (!removed.has(String(fileId))) {
            const result = await deleteFile(id, fileId);
            if (result?.status !== 'ok') throw new Error(result?.detail || 'Не удалось убрать файл из черновика');
            removed.add(String(fileId));
          }
          onDelete?.(fileId);
        }
        // If a reply was lost, removing the local row does not prove that the
        // server did not save it. Resolve its stable receipt, then delete it.
        for (const [file, state] of uploads) {
          if (!state.attempted || state.cancelled || snapshot.input_files.includes(file)
              || snapshot.existing_files.some(item => sameCityId(item.id, state.receipt?.id))) continue;
          if (!state.receipt) {
            const result = await uploadFile(id, file, true, null, state.token);
            if (result?.status === 'error' || !result?.id) throw new Error(`Не удалось подтвердить удаление файла «${file.name}». Повторите сохранение.`);
            state.receipt = result;
          }
          const fileId = state.receipt.id;
          if (!removed.has(String(fileId))) {
            const result = await deleteFile(id, fileId);
            if (result?.status !== 'ok') throw new Error(result?.detail || `Не удалось убрать файл «${file.name}» из черновика`);
            removed.add(String(fileId));
          }
          state.cancelled = true;
          onDelete?.(fileId);
        }
        for (const file of snapshot.input_files) {
          const state = uploadState(file);
          if (!state.receipt) {
            state.attempted = true;
            const result = await uploadFile(id, file, true, null, state.token);
            if (result?.status === 'error' || !result?.id) {
              throw new Error(`Не удалось сохранить файл «${file.name}» в черновике. ${result?.detail || 'Повторите сохранение.'}`);
            }
            state.receipt = result;
          }
          onFile?.(file, state.receipt);
        }
        return { id, application: lastApplication };
      };
      // A failed network attempt does not poison the queue. Retrying the same
      // File reuses its token, including when the server committed a lost reply.
      const result = tail.catch(() => undefined).then(run);
      tail = result;
      return result;
    },
  };
}
